import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { IAICampaignBrief, IAICampaignSendFrom } from '../../models/AICampaign';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { invalidateAICampaignQuota } from './useAICampaignQuota';

interface CreateAICampaignVariables {
	name?: string;
	brief: IAICampaignBrief;
	/**
	 * The audience, as any campaign expresses it. Unsubscribed and bounced contacts are excluded server-side.
	 * Omitted when the audience is only individually chosen contacts, which need no filter.
	 */
	audience?: Api.ContactFilter | null;
	/** Who the campaign sends as; omitted means the creator. Validated server-side against send-on-behalf rights. */
	sendFrom?: IAICampaignSendFrom;
	/** Contacts struck from the audience during recipient review, dropped when the audience is frozen. */
	excludedContactIds?: string[];
	/**
	 * Contacts picked individually rather than matched by the filter. No filter property selects contacts by id, so
	 * these travel separately and the server unions them with whatever the filter selects.
	 */
	contactIds?: string[];
	/**
	 * Which contacts the sender is scoping to. Its own field rather than a criterion in `audience`, because contact
	 * resolution reads it separately and treats an ownership filter of All as no filter at all.
	 */
	ownership?: Api.IOwnershipFilter;
}

export const useCreateAICampaign = ({
	onError,
	onSuccess,
}: {
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: (campaign: Api.ICampaign) => void;
} = {}) => {
	const userSession = useUserSession();

	return useMutation<Api.ICampaign, Api.IOperationResultNoValue, CreateAICampaignVariables>({
		mutationFn: variables => {
			return userSession.webServiceHelper.callAsync<Api.ICampaign>('AICampaign', 'POST', variables);
		},
		onError,
		onSuccess: campaign => {
			invalidateAICampaignQuota();
			onSuccess?.(campaign);
		},
	});
};
